import {DocumentationImage} from "../../../api/models";

// HTML written elsewhere (docs/public/products/*) refers to the library's images as <img src="doc-image:{name}">; the
// API turns those into the images' ids when saving (tristar-corp-api's DocumentationImages).
export const DOC_IMAGE_SCHEME = 'doc-image:';
// PNG and JPEG only (the PDF can embed those), up to 10 MB, as the API checks them.
export const DOC_IMAGE_TYPES = ['image/png', 'image/jpeg'];
export const DOC_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const NAME_MAX_LENGTH = 64;

// A library name from a file's, as the API checks them: its base name in lower case, with dashes for anything else
// ('Filter Panel.png' → 'filter-panel').
export function docImageName(fileName: string): string {
    const name = (fileName.split(/[\\/]/).pop() ?? '').replace(/\.[^.]*$/, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').substring(0, NAME_MAX_LENGTH).replace(/^-+|-+$/g, '');
    return name || 'image';
}

export function isValidDocImageName(name: string): boolean {
    return name.length <= NAME_MAX_LENGTH && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(name);
}

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

// What to write in documentation HTML to show the image.
export function docImageMarkup(image: DocumentationImage): string {
    return `<img src="${DOC_IMAGE_SCHEME}${image.name}" alt="${escapeAttribute(image.altText)}">`;
}

const REFERENCE = /(<img\b[^>]*?\bsrc=")doc-image:([^"]*)(")/gi;

// The doc-image: references in html, as written.
export function docImageReferences(html: string): string[] {
    return [...html.matchAll(REFERENCE)].map(x => x[2]);
}

const LINK = /(<img\b[^>]*?\bsrc=")(https:[^"]*\/docs-images\/[^"/]+\/(dim_[0-9a-f]+)(?:-[0-9a-f]+)?\.\w+)(")/gi;

// html with each doc-image: reference to an image in the library (by name or id) as its link, so the editor can show it,
// and links to an image whose picture has since been replaced as its new link. Saving turns links back into ids. Other
// references are left as they are (saving leaves them out, and says which they were).
export function withDocImageLinks(html: string, images: DocumentationImage[]): string {
    if (!html.includes(DOC_IMAGE_SCHEME) && !html.includes('/docs-images/')) {
        return html;
    }
    return html
        .replace(REFERENCE, (whole, before: string, reference: string, after: string) => {
            const image = images.find(x => x.id === reference) ?? images.find(x => x.name === reference);
            return image ? `${before}${escapeAttribute(image.src)}${after}` : whole;
        })
        .replace(LINK, (whole, before: string, src: string, id: string, after: string) => {
            const image = images.find(x => x.id === id);
            return image && image.src !== src ? `${before}${escapeAttribute(image.src)}${after}` : whole;
        });
}
